import 'react-datepicker/dist/react-datepicker.css';
import './SearchDatePicker.scss';

import { IonIcon } from '@ionic/react';
import {
  addDays, addMonths, differenceInCalendarDays, differenceInCalendarMonths, format,
} from 'date-fns';
import es from 'date-fns/locale/es';
import React, {
  forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import DatePicker from 'react-datepicker';

import CloseIcon from '@/assets/icons/close-outline.svg';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import { AvailableSpecificFilters, getSpecificFiltersLabel } from '@/utils/dashboardDateRange';

import SearchScopeCalendar, { SearchScopeContext } from './SearchScopeCalendar';

interface SearchDatePickerProps {
  dateStart: (date: string) => void;
  dateEnd: (date: string) => void;
  savedStartDate: string;
  savedEndDate: string;
  clear: boolean;
  monthsShown?: number;
  minDate?: Date;
  // Días máximos entre inicio y fin mientras se elige la fecha de fin
  maxRangeDays?: number;
  errorId?: string;
  // Buscadores específicos visibles en el dashboard, para el aviso de 3 años del calendario
  availableSpecificFilters?: AvailableSpecificFilters;
}

export interface SearchDatePickerRef {
  openDatePicker: () => void;
}

const SearchDatePicker = forwardRef<SearchDatePickerRef, SearchDatePickerProps>(({
  dateStart,
  dateEnd,
  savedStartDate,
  savedEndDate,
  clear,
  monthsShown = 2,
  minDate,
  maxRangeDays,
  errorId,
  availableSpecificFilters,
}, ref) => {
  const datePickerRef = useRef<DatePicker>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    savedStartDate ? new Date(savedStartDate) : null,
    savedEndDate ? new Date(savedEndDate) : null,
  ]);
  const [startDate, endDate] = dateRange;

  const formattedDate = (date: string) => format(new Date(date), 'dd/MM/yyyy');
  const isDesktop = useMediaQuery(Breakpoint.Desktop);

  const today = new Date();
  const rangeLimit = startDate && !endDate && maxRangeDays
    ? addDays(startDate, maxRangeDays)
    : null;
  const maxDate = rangeLimit && rangeLimit < today ? rangeLimit : today;

  const specificFiltersLabel = getSpecificFiltersLabel(availableSpecificFilters);
  const searchScope = useMemo(() => ({
    startDate: clear ? startDate : null,
    endDate: clear ? endDate : null,
    limited: !!maxRangeDays,
    specificFiltersLabel,
  }), [clear, startDate, endDate, maxRangeDays, specificFiltersLabel]);

  const modDate = (update: [Date | null, Date | null]) => {
    const [newStartDate, newEndDate] = update;
    if (newStartDate !== startDate || newEndDate !== endDate) {
      setDateRange(update);
      if (update[0] === null && update[1] === null) {
        dateStart('');
        dateEnd('');
      } else {
        const formattedStartDate = newStartDate
          ? format(newStartDate, 'yyyy-MM-dd')
          : '';
        const formattedEndDate = newEndDate
          ? format(newEndDate, 'yyyy-MM-dd')
          : '';
        dateStart(formattedStartDate);
        dateEnd(formattedEndDate);
      }
    }
  };

  const clearDatePicker = () => {
    dateStart('');
    dateEnd('');
    setDateRange([null, null]);
  };

  useImperativeHandle(ref, () => ({
    openDatePicker: () => {
      // Los clics dentro del calendario (aunque esté en un portal) llegan al onClick
      // del contenedor que lo abre: reabrirlo ya abierto recoloca los meses
      if (datePickerRef.current?.isCalendarOpen()) {
        return;
      }
      datePickerRef.current?.setOpen(true);
    },
  }));

  const CustomInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
    ({
      value, onClick, 'aria-invalid': ariaInvalid, 'aria-describedby': ariaDescribedBy,
    }, ref) => (
      <input
        className="custom-datepicker-input"
        value={value || ''}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder="Fechas"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
    ),
  );

  return (
    <SearchScopeContext.Provider value={searchScope}>
      <div className="search-date-picker-container">
        {(savedStartDate || savedEndDate)
        && (
        <IonIcon
          onClick={(e) => {
            e.stopPropagation();
            clearDatePicker();
          }}
          src={CloseIcon}
        />
        )}
        <DatePicker
          ref={datePickerRef}
          minDate={minDate}
          maxDate={maxDate}
          ariaInvalid={errorId ? 'true' : undefined}
          ariaDescribedBy={errorId}
          className={`datepicker-wrapper ${clear && savedStartDate && savedEndDate ? 'with-value' : ''}`}
          locale={es}
          selectsRange
          startDate={clear && startDate ? startDate : undefined}
          endDate={clear && endDate ? endDate : undefined}
          dateFormat="dd/MM/yyyy"
          onChange={modDate}
          value={clear && savedStartDate && savedEndDate ? `${formattedDate(savedStartDate)} - ${formattedDate(savedEndDate)}` : ''}
          withPortal
          // Al elegir la fecha de fin, react-datepicker devuelve el foco al input en
          // un setTimeout que puede llegar tarde y reabrir el calendario (parpadeo).
          // Sin abrir al enfocar, se abre con clic o con Enter/flechas (onKeyDown)
          preventOpenOnFocus
          onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
            if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)
              && !datePickerRef.current?.isCalendarOpen()) {
              datePickerRef.current?.setOpen(true);
            }
          }}
          // El último mes visible es el actual (o el del límite del rango): los
          // meses futuros no se muestran porque no se pueden seleccionar
          showPreviousMonths
          // Con un rango elegido se abre en el mes de la fecha de fin
          openToDate={clear && startDate && endDate ? endDate : undefined}
          // react-datepicker recuerda en qué mes se hizo clic y, sin reiniciarlo, al
          // reabrir podría colocar el mes actual a la izquierda y enseñar el siguiente
          onCalendarClose={() => datePickerRef.current?.setState({ monthSelectedIn: undefined })}
          // Siempre 6 semanas para que el alto no cambie según el mes
          fixedHeight
          dayClassName={(date: Date) => (differenceInCalendarDays(date, today) > 0
            ? 'react-datepicker__day--future'
            : '')}
          placeholderText="Fechas"
          customInput={!isDesktop ? <CustomInput /> : undefined}
          renderCustomHeader={({
            monthDate,
            customHeaderCount,
            decreaseMonth,
            increaseMonth,
          }: any) => {
            const lastVisibleMonth = addMonths(monthDate, monthsShown - 1 - customHeaderCount);
            const nextMonthDisabled = differenceInCalendarMonths(maxDate, lastVisibleMonth) <= 0;
            // Una sola flecha a cada lado: anterior en el primer mes, siguiente en el último
            const isFirstMonth = customHeaderCount === 0;
            const isLastMonth = customHeaderCount === monthsShown - 1;

            return (
              <div>
                {isFirstMonth && (
                <button
                  aria-label="Previous Month"
                  className="react-datepicker__navigation react-datepicker__navigation--previous"
                  onClick={decreaseMonth}
                >
                  <span
                    className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"
                  >
                    {'<'}
                  </span>
                </button>
                )}
                <span className="react-datepicker__current-month">
                  {monthDate
                    .toLocaleString('es-ES', {
                      month: 'long',
                      year: 'numeric',
                    })
                    .replace(/^\w/, (c: any) => c.toUpperCase())}
                </span>
                {isLastMonth && !nextMonthDisabled && (
                <button
                  aria-label="Next Month"
                  className="react-datepicker__navigation react-datepicker__navigation--next"
                  onClick={increaseMonth}
                >
                  <span
                    className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next"
                  >
                    {'>'}
                  </span>
                </button>
                )}
              </div>
            );
          }}
          selected={clear && startDate ? startDate : undefined}
          monthsShown={monthsShown}
          calendarContainer={SearchScopeCalendar}
        />
      </div>
    </SearchScopeContext.Provider>
  );
});

export default SearchDatePicker;
